import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useApiClient from '../hooks/useApiClient';

const GDPR = () => {
  const { shopId } = useParams();
  const [currentStep, setCurrentStep] = useState(1); // Track current step
  const [consentChecked, setConsentChecked] = useState(false);
  const [agreement, setAgreement] = useState({
    agree: false,
    disagree: false
  });
  const [userData, setUserData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [shopData, setShopData] = useState({
    brandName: '[Numele Atelierului]',
    description: '[adresă]',
    shopId: '',
    startTime: '',
    endTime: ''
  });
  const [loading, setLoading] = useState(!!shopId);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: '',
    phone: ''
  });
  const apiClient = useApiClient();
  
  // Fetch shop data if shopId is provided
  useEffect(() => {
    if (shopId) {
      const fetchShopData = async () => {
        try {
          setLoading(true);
          const response = await apiClient.get(`/gdpr/${shopId}`);
          console.log("Shop data response:", response);
          if (response) {
            setShopData(response);
          }
        } catch (error) {
          console.error('Error fetching shop data:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchShopData();
    }
  }, [shopId]);
  
  // Other handlers
  const handleConsentChange = () => {
    setConsentChecked(!consentChecked);
  };

  const handleAgreementChange = (type) => {
    if (type === 'agree') {
      setAgreement({ agree: true, disagree: false });
    } else {
      setAgreement({ agree: false, disagree: true });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear the error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateFirstStep = () => {
    let isValid = true;
    const newErrors = { name: '', phone: '', email: '' };
    
    if (!userData.name.trim()) {
      newErrors.name = 'Numele este obligatoriu';
      isValid = false;
    }
    
    if (!userData.phone.trim()) {
      newErrors.phone = 'Numărul de telefon este obligatoriu';
      isValid = false;
    }
    if (!userData.email.trim()) {
        newErrors.email = 'Emailul este obligatoriu';
        isValid = false;
      }

    setErrors(newErrors);
    return isValid;
  };

  const goToNextStep = () => {
    if (validateFirstStep()) {
      setCurrentStep(2);
      window.scrollTo(0, 0); // Scroll to top when changing steps
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep(1);
    window.scrollTo(0, 0); // Scroll to top when changing steps
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if either agreement option is selected
    if (agreement.agree || agreement.disagree) {
      try {
        setSubmitLoading(true);
        
        // Send POST request to API directly with the data
        await apiClient.post(`/gdpr/${shopId}`, {
          name: userData.name,
          phone: userData.phone,
          email: userData.email,
          marketingConsent: consentChecked, // Already false by default when not checked
          gdprConsent: agreement.agree // Will be true or false depending on which option was selected
        });
        
        console.log("Form submitted with data:", userData);
        console.log("Marketing consent:", consentChecked);
        console.log("GDPR consent:", agreement.agree);
        console.log("Shop data:", shopData);
        
        if (agreement.agree) {
          alert("Ați fost de acord cu termenii GDPR. Vă mulțumim!");
        } else {
          alert("Am înregistrat că nu sunteți de acord cu prelucrarea datelor personale.");
        }
        
        // Reload the page after successful submission
        window.location.reload();
        
      } catch (error) {
        console.error("Error submitting GDPR consent:", error);
        alert("A apărut o eroare la transmiterea datelor. Vă rugăm să încercați din nou.");
      } finally {
        setSubmitLoading(false);
      }
    } else {
      alert("Vă rugăm să selectați dacă sunteți de acord sau nu cu prelucrarea datelor.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-5 font-sans text-gray-700 bg-white rounded-lg min-h-screen flex flex-col">
      {/* Step indicator */}
      <div className="mb-6 flex justify-center">
        <div className="flex items-center">
          <div className={`rounded-full h-8 w-8 flex items-center justify-center ${
            currentStep === 1 ? 'bg-purple-600 text-white' : 'bg-purple-200 text-purple-700'
          }`}>
            1
          </div>
          <div className="h-1 w-16 mx-2 bg-purple-200">
            {currentStep === 2 && <div className="h-full bg-purple-600" style={{width: '100%'}}></div>}
          </div>
          <div className={`rounded-full h-8 w-8 flex items-center justify-center ${
            currentStep === 2 ? 'bg-purple-600 text-white' : 'bg-purple-200 text-purple-700'
          }`}>
            2
          </div>
        </div>
      </div>

      {/* Step 1: User identification section */}
      {currentStep === 1 && (
        <>
          <h1 className="text-2xl text-gray-800 border-b-2 border-gray-100 pb-2.5 mb-5">
            Pas 1: Date de identificare
          </h1>
          
          <div className="mb-8 p-4 bg-gray-50 rounded-md">
            <h2 className="text-xl text-purple-600 mb-4">Date de identificare</h2>
            <p className="mb-3 font-medium">Subsemnatul/a:</p>
            <form className="space-y-3">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nume și prenume
                </label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={userData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  required 
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telefon
                </label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone"
                  value={userData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  required 
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  value={userData.email}
                  required
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}

              </div>
            </form>
          </div>
          
          <div className="flex justify-end">
            <button 
              onClick={goToNextStep}
              className="py-2 px-6 rounded-md text-white font-medium bg-purple-600 hover:bg-purple-700 transition-colors"
            >
              Continuă
            </button>
          </div>
        </>
      )}

      {/* Step 2: GDPR Information and Consent */}
      {currentStep === 2 && (
        <>
          <h1 className="text-2xl text-gray-800 border-b-2 border-gray-100 pb-2.5 mb-5">
            Pas 2: Formular GDPR – {shopData.brandName || '[Numele Atelierului]'}
          </h1>
          
          <div className="mb-6">
            <p><strong>Responsabil cu protecția datelor:</strong> {shopData.email} / {shopData.phoneNumber}</p>
          </div>

          {/* Display user data summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <h3 className="text-lg font-medium mb-2">Date de identificare introduse:</h3>
            <p><strong>Nume și prenume:</strong> {userData.name}</p>
            <p><strong>Telefon:</strong> {userData.phone}</p>
            {userData.email && <p><strong>Email:</strong> {userData.email}</p>}
          </div>

          <div className="mb-6">
            <h2 className="text-xl text-purple-500 mb-4">1. Cine suntem</h2>
            <p>Atelierul auto <strong>{shopData.brandName || '[Numele Atelierului]'}</strong>, cu sediul în {shopData.address || '[adresă]'}, CUI: {shopData.taxIdentifier} colectează și procesează datele dumneavoastră personale în scopul programărilor și notificărilor privind serviciile auto.</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl text-purple-500 mb-4">2. Ce date colectăm</h2>
            <ul className="list-disc pl-5 leading-relaxed">
              <li>Nume și prenume</li>
              <li>Număr de telefon</li>
              <li>Adresă de e-mail (dacă este furnizată)</li>
              <li>Numărul de înmatriculare al vehiculului</li>
              <li>Data programării și tipul serviciului</li>
              <li>Date despre expirarea ITP, RCA, etc.</li>
            </ul>
          </div>

          <div className="mb-6">
            <h2 className="text-xl text-purple-500 mb-4">3. Scopul prelucrării</h2>
            <p>Prelucrăm aceste date pentru:</p>
            <ul className="list-disc pl-5 leading-relaxed">
              <li>Gestionarea programărilor</li>
              <li>Ținerea evidențelor intervențiilor conform legii</li>
              <li>Transmiterea notificărilor privind programările și expirarea documentelor</li>
              <li>Comunicare directă legată de serviciile solicitate</li>
            </ul>
            <p className="italic">Bază legală: interes legitim și/sau executarea unui contract (Art. 6 alin. 1 lit. f și b din <a href="https://eur-lex.europa.eu/legal-content/RO/TXT/PDF/?uri=CELEX:32016R0679" className="text-purple-500 no-underline hover:underline">GDPR</a>)</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl text-purple-500 mb-4">4. Cât timp păstrăm datele</h2>
            <p>Datele vor fi păstrate maximum 3 ani de la ultima interacțiune, sau conform cerințelor legale.</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl text-purple-500 mb-4">5. Cine are acces la date</h2>
            <p>Datele pot fi accesate doar de personalul autorizat și de furnizorii tehnici implicați în buna funcționare a aplicației.</p>
            <p><strong>Pentru gestionarea programărilor și trimiterea notificărilor</strong>, folosim infrastructura oferită de <a href="servicegest.ro" className="text-purple-500 no-underline hover:underline"><strong>Servicegest</strong></a>, un furnizor contractual al aplicației, care procesează datele exclusiv în scopul prestării serviciilor digitale.</p>
            <p>Acești parteneri acționează ca <em>împuterniciți conform GDPR</em> și sunt obligați contractual să respecte confidențialitatea și securitatea datelor.</p>
          </div>

          <div className="mb-6">
            <h2 className="text-xl text-purple-500 mb-4">6. Drepturile dumneavoastră</h2>
            <ul className="list-disc pl-5 leading-relaxed">
              <li>Dreptul de acces, rectificare și ștergere</li>
              <li>Dreptul la restricționarea sau opoziția prelucrării</li>
              <li>Dreptul la portabilitatea datelor</li>
              <li>Dreptul de a depune o plângere la ANSPDCP</li>
            </ul>
            <p>Pentru orice solicitare, ne puteți contacta la <strong>{shopData.email}</strong>.</p>
          </div>

          <div className="mb-8">
            <h2 className="text-xl text-purple-500 mb-4">7. Consimțământ pentru comunicări comerciale (opțional)</h2>
            <div className="flex items-start mt-4 p-4 bg-gray-50 rounded-md">
              <input
                type="checkbox"
                id="consimtamant"
                name="consimtamant"
                checked={consentChecked}
                onChange={handleConsentChange}
                className="mr-2 mt-1"
              />
              <label htmlFor="consimtamant">
                Sunt de acord să primesc oferte și promoții din partea atelierului {shopData.brandName || '[Numele Atelierului]'} și a Servicegest
              </label>
            </div>
          </div>

          {/* GDPR Agreement section */}
          <div className="mt-8 p-4 bg-purple-50 rounded-md border border-purple-200">
            <p className="font-medium mb-4">
              Am citit politica de prelucrare a datelor cu caracter personal și:
            </p>
            
            <p className="text-sm italic mb-4">
              Subsemnatul {userData.name}, cu numărul de telefon {userData.phone}{userData.email ? ` și adresa de email ${userData.email}` : ''}, 
              declar că am fost informat cu privire la prelucrarea datelor cu caracter personal de către {shopData.brandName || '[Numele Atelierului]'} în scopurile menționate mai sus.
            </p>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-start p-3 rounded-md bg-white border border-purple-100">
                <input
                  type="checkbox"
                  id="gdprAgree"
                  checked={agreement.agree}
                  onChange={() => handleAgreementChange('agree')}
                  className="mr-3 mt-1"
                />
                <label htmlFor="gdprAgree" className="font-medium">
                  Sunt de acord cu prelucrarea datelor mele personale
                </label>
              </div>
              
              <div className="flex items-start p-3 rounded-md bg-white border border-purple-100">
                <input
                  type="checkbox"
                  id="gdprDisagree"
                  checked={agreement.disagree}
                  onChange={() => handleAgreementChange('disagree')}
                  className="mr-3 mt-1"
                />
                <label htmlFor="gdprDisagree" className="font-medium">
                  Nu sunt de acord cu prelucrarea datelor mele personale
                </label>
              </div>
            </div>
            
            <div className="flex justify-between">
              <button 
                onClick={goToPreviousStep}
                className="py-2 px-4 rounded-md text-purple-700 font-medium bg-white border border-purple-300 hover:bg-gray-50 transition-colors"
              >
                Înapoi
              </button>
              
              <button 
                onClick={handleSubmit}
                disabled={(!agreement.agree && !agreement.disagree) || submitLoading}
                className={`py-2 px-6 rounded-md text-white font-medium ${
                  (!agreement.agree && !agreement.disagree) || submitLoading
                    ? 'bg-purple-300 cursor-not-allowed' 
                    : 'bg-purple-600 hover:bg-purple-700'
                } transition-colors flex items-center justify-center`}
              >
                {submitLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Se trimite...
                  </>
                ) : (
                  'Trimite'
                )}
              </button>
            </div>
          </div>
          
          {/* Remove the shop operating hours display section */}
        </>
      )}
      
      {/* Footer with attribution */}
      <div className="mt-auto pt-4 border-t border-gray-100 text-center text-sm text-gray-500">
        <p>
          Made with <span className="text-pink-500">💜</span> by <a href="https://servicegest.ro" target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:text-purple-700 transition-colors">Servicegest</a>
        </p>
      </div>
    </div>
  );
};

export default GDPR;



